import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Types
export interface MessageTemplate {
  id: number;
  brand_id: string;
  template_name: string;
  template_content: string;
  template_type: string;
  platform?: string;
  category?: string;
  sentiment?: string;
  is_active: boolean;
  is_default: boolean;
  usage_count: number;
  variables?: Record<string, string>;
  description?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  brand_name?: string;
}

export interface CreateMessageTemplateRequest {
  brand_id: string;
  template_name: string;
  template_content: string;
  template_type: string;
  platform?: string;
  category?: string;
  sentiment?: string;
  is_active?: boolean;
  is_default?: boolean;
  variables?: Record<string, string>;
  description?: string;
}

export interface UpdateMessageTemplateRequest extends Partial<CreateMessageTemplateRequest> {
  id: number;
}

export interface MessageTemplateFilters {
  page?: number;
  limit?: number;
  template_type?: string;
  platform?: string;
  category?: string;
  sentiment?: string;
  is_active?: boolean;
  search?: string;
}

export interface TemplateUsageAnalytics {
  id: number;
  template_name: string;
  template_type: string;
  platform?: string;
  category?: string;
  sentiment?: string;
  usage_count: number;
  customized_count: number;
  last_used?: string;
}

export interface ProcessTemplateRequest {
  variables: Record<string, string>;
}

export interface ProcessTemplateResponse {
  template_id: number;
  original_content: string;
  processed_content: string;
  variables: Record<string, string>;
}

export interface SuggestedTemplatesResponse {
  suggested_templates: MessageTemplate[];
  context: {
    thread_id: string;
    brand_id: string;
    customer_username?: string;
    detected_sentiment: string;
    detected_category: string;
    template_type: string;
  };
}

// API functions
export const messageTemplatesAPI = {
  // Get all templates for a brand
  getTemplates: async (brandId: string, filters?: MessageTemplateFilters) => {
    try {
      const response = await axios.get(`${API_URL}/message-templates/brand/${brandId}`, {
        params: filters,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching message templates:', error);
      throw error;
    }
  },

  // Get template by ID
  getTemplateById: async (id: number) => {
    try {
      const response = await axios.get(`${API_URL}/message-templates/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching message template:', error);
      throw error;
    }
  },

  // Create new template
  createTemplate: async (templateData: CreateMessageTemplateRequest) => {
    try {
      const response = await axios.post(`${API_URL}/message-templates`, templateData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating message template:', error);
      throw error;
    }
  },

  // Update template
  updateTemplate: async (id: number, templateData: Partial<CreateMessageTemplateRequest>) => {
    try {
      const response = await axios.put(`${API_URL}/message-templates/${id}`, templateData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error updating message template:', error);
      throw error;
    }
  },

  // Delete template
  deleteTemplate: async (id: number) => {
    try {
      const response = await axios.delete(`${API_URL}/message-templates/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error deleting message template:', error);
      throw error;
    }
  },

  // Get suggested templates for context
  getSuggestedTemplates: async (
    brandId: string, 
    platform?: string, 
    category?: string, 
    sentiment?: string, 
    template_type?: string
  ) => {
    try {
      const response = await axios.get(`${API_URL}/message-templates/brand/${brandId}/suggested`, {
        params: { platform, category, sentiment, template_type },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching suggested templates:', error);
      throw error;
    }
  },

  // Get suggested templates for a DM thread
  getSuggestedTemplatesForThread: async (threadId: string, template_type?: string) => {
    try {
      const response = await axios.get(`${API_URL}/direct-messages/thread/${threadId}/templates`, {
        params: { template_type },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching suggested templates for thread:', error);
      throw error;
    }
  },

  // Process template with variables
  processTemplate: async (templateId: number, variables: Record<string, string>) => {
    try {
      const response = await axios.post(`${API_URL}/message-templates/${templateId}/process`, {
        variables
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error processing template:', error);
      throw error;
    }
  },

  // Record template usage
  recordTemplateUsage: async (
    templateId: number, 
    used_in_platform: string, 
    used_in_context: string, 
    target_entity_id: string, 
    customized_content?: string, 
    was_customized?: boolean
  ) => {
    try {
      const response = await axios.post(`${API_URL}/message-templates/${templateId}/usage`, {
        used_in_platform,
        used_in_context,
        target_entity_id,
        customized_content,
        was_customized
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error recording template usage:', error);
      throw error;
    }
  },

  // Get template usage analytics
  getTemplateUsageAnalytics: async (
    brandId: string, 
    start_date?: string, 
    end_date?: string, 
    template_id?: number
  ) => {
    try {
      const response = await axios.get(`${API_URL}/message-templates/brand/${brandId}/analytics`, {
        params: { start_date, end_date, template_id },
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching template usage analytics:', error);
      throw error;
    }
  }
};

// Helper functions
export const templateHelpers = {
  // Extract variables from template content
  extractVariables: (content: string): string[] => {
    const matches = content.match(/\{\{([^}]+)\}\}/g);
    return matches ? matches.map(match => match.slice(2, -2)) : [];
  },

  // Replace variables in template content
  replaceVariables: (content: string, variables: Record<string, string>): string => {
    let result = content;
    for (const [key, value] of Object.entries(variables)) {
      const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(regex, value);
    }
    return result;
  },

  // Validate template content
  validateTemplate: (content: string): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    // Check for unmatched braces
    const openBraces = (content.match(/\{\{/g) || []).length;
    const closeBraces = (content.match(/\}\}/g) || []).length;
    
    if (openBraces !== closeBraces) {
      errors.push('Unmatched template braces');
    }
    
    // Check for empty content
    if (!content.trim()) {
      errors.push('Template content cannot be empty');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
};

export default messageTemplatesAPI;